import { getMomentTz, useUTLSettingsContext } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { ReactNode, useEffect, useRef } from "react";
import { getShifts } from "../shared/repositories/ShiftRepository";

const moment = getMomentTz();

const SHIFT_CHECK_INTERVAL = 60000; // Check every minute
const SHIFT_REFRESH_INTERVAL = 1800000; // Refresh shift data every 30 minutes
const SHIFT_STORAGE_KEY = "shiftAutoLogout";
const AUTO_LOGOUT_SETTING_KEY = "SYSTEM.AUTO_LOGOUT_ON_SHIFT_CHANGE";

interface ShiftLogoutData {
	shiftEndTime: string;
	lastCheckedShiftId: number;
}

export const AutoLogout = ({ children }: { children: ReactNode }) => {
	const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
	const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

	// Get UTL settings to check if auto-logout is enabled
	const { state } = useUTLSettingsContext();
	const isAutoLogoutEnabled = !!get(
		state,
		`settings["${AUTO_LOGOUT_SETTING_KEY}"]`,
		false,
	);

	const fetchAndStoreShiftData = async () => {
		// Only fetch shift data if auto-logout is enabled
		if (!isAutoLogoutEnabled) {
			return;
		}

		try {
			// Fetch current shift (passing null gets current shift)
			const shifts = await getShifts({ productionDate: null });

			if (shifts && shifts.length > 0) {
				const currentShift = shifts[0];

				// Store shift end time and ID in localStorage
				const shiftData: ShiftLogoutData = {
					shiftEndTime: moment(currentShift.CurrentEnd).toISOString(),
					lastCheckedShiftId: currentShift.CurrentShiftId,
				};

				localStorage.setItem(SHIFT_STORAGE_KEY, JSON.stringify(shiftData));
				console.log(
					`Shift auto-logout scheduled for: ${currentShift.CurrentEnd}`,
				);
			}
		} catch (error) {
			console.error("Failed to fetch shift data for auto-logout:", error);
			// Retry after 5 minutes on error
			setTimeout(fetchAndStoreShiftData, 300000);
		}
	};

	const checkShiftEnd = () => {
		// Only check shift end if auto-logout is enabled
		if (!isAutoLogoutEnabled) {
			return;
		}

		const storedData = localStorage.getItem(SHIFT_STORAGE_KEY);

		if (storedData) {
			try {
				const shiftData: ShiftLogoutData = JSON.parse(storedData);
				const shiftEndTime = moment.utc(shiftData.shiftEndTime);
				const now = moment();

				// Check if current time has passed shift end time
				if (now.isAfter(shiftEndTime)) {
					console.log("Shift has ended. Logging out...");

					// Clear the shift data from localStorage
					localStorage.removeItem(SHIFT_STORAGE_KEY);

					// Clear intervals
					if (checkIntervalRef.current) {
						clearInterval(checkIntervalRef.current);
					}
					if (refreshIntervalRef.current) {
						clearInterval(refreshIntervalRef.current);
					}

					// Redirect to logout with message
					window.location.href = "/logout?message=Shift%20Change";
				}
			} catch (error) {
				console.error("Error parsing shift logout data:", error);
				localStorage.removeItem(SHIFT_STORAGE_KEY);
			}
		}
	};

	useEffect(() => {
		// Only set up auto-logout if enabled in settings
		if (!isAutoLogoutEnabled) {
			console.log("Auto-logout on shift change is disabled");
			// Clean up any existing data
			localStorage.removeItem(SHIFT_STORAGE_KEY);
			return;
		}

		console.log("Auto-logout on shift change is enabled");

		// Initial fetch of shift data
		fetchAndStoreShiftData();

		// Set up interval to check shift end every minute
		checkIntervalRef.current = setInterval(checkShiftEnd, SHIFT_CHECK_INTERVAL);

		// Set up interval to refresh shift data every 30 minutes
		refreshIntervalRef.current = setInterval(
			fetchAndStoreShiftData,
			SHIFT_REFRESH_INTERVAL,
		);

		// Cleanup on unmount
		return () => {
			if (checkIntervalRef.current) {
				clearInterval(checkIntervalRef.current);
			}
			if (refreshIntervalRef.current) {
				clearInterval(refreshIntervalRef.current);
			}
		};
	}, [isAutoLogoutEnabled]);

	return <>{children}</>;
};
